/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/components/Sidebar.jsx
 * Description  : Navigation sidebar
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  {
    section: 'หลัก',
    items: [
      { to: '/', label: 'Dashboard', icon: HomeIcon },
      { to: '/records', label: 'รายการสำรองจ่าย', icon: ListIcon, badge: 'pending' },
    ],
  },
  {
    section: 'รายงาน & แจ้งเตือน',
    items: [
      { to: '/report',        label: 'รายงาน',                icon: ChartIcon },
      { to: '/notifications', label: 'การแจ้งเตือน',          icon: BellIcon, badge: 'notif', adminOnly: true },
      { to: '/history',       label: 'ประวัติการดำเนินการ',   icon: ClockIcon },
      {
        label: 'ตรวจสอบยอด', icon: ScaleIcon,
        children: [
          { to: '/reconciliation',         label: 'SAP & Bank' },
          { to: '/reconciliation/history', label: 'รายงาน' },
        ],
      },
    ],
  },
  {
    section: 'ระบบ',
    items: [
      { to: '/companies', label: 'บริษัท',       icon: BuildingIcon },
      { to: '/users',     label: 'ผู้ใช้งาน',    icon: UsersIcon },
      { to: '/settings',  label: 'ตั้งค่าระบบ',  icon: GearIcon, adminOnly: true },
    ],
  },
];

export default function Sidebar({ pendingCount, notifCount, currentUser }) {
  const location = useLocation();
  const isAdmin = currentUser?.role === 'admin';
  // Manually-toggled-open parent items (by label) — a parent whose child is
  // the active route is always shown open regardless of this set.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (label) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  return (
    <aside
      className="flex flex-col bg-white border-r border-gray-200 shrink-0"
      style={{ width: 210 }}
    >
      {/* Logo */}
      <div className="px-5 py-5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 text-base leading-tight">CSAF</div>
            <div className="text-gray-400 leading-tight whitespace-nowrap" style={{ fontSize: 11 }}>Charoensin Alert Finance</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV_ITEMS.map((group) => (
          <div key={group.section} className="mb-3">
            <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {group.section}
            </div>
            {group.items.filter((item) => !item.adminOnly || isAdmin).map((item) => {
              if (item.children) {
                const isChildActive = item.children.some((c) => location.pathname === c.to);
                const isOpen = isChildActive || expanded.has(item.label);
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => toggleExpanded(item.label)}
                      className={`flex items-center gap-3 w-full mx-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 mb-0.5
                        ${isChildActive
                          ? 'text-primary'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      style={{ width: 'calc(100% - 16px)' }}
                    >
                      <item.icon className={`w-4 h-4 shrink-0 ${isChildActive ? 'text-primary' : 'text-gray-400'}`} />
                      <span className="flex-1 truncate text-left">{item.label}</span>
                      <ChevronIcon className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="ml-4 pl-4 border-l border-gray-100">
                        {item.children.map((child) => {
                          const childActive = location.pathname === child.to;
                          return (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              className={`block mx-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 mb-0.5 truncate
                                ${childActive
                                  ? 'bg-primary-light text-primary'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                            >
                              {child.label}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive =
                item.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.to);
              const badgeVal =
                item.badge === 'pending'
                  ? pendingCount
                  : item.badge === 'notif'
                  ? notifCount
                  : 0;

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 mb-0.5
                    ${isActive
                      ? 'bg-primary-light text-primary'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  <item.icon
                    className={`w-4 h-4 shrink-0 ${isActive ? 'text-primary' : 'text-gray-400'}`}
                  />
                  <span className="flex-1 truncate">{item.label}</span>
                  {badgeVal > 0 && (
                    <span className="ml-auto bg-danger text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0 badge-pulse">
                      {badgeVal > 99 ? '99+' : badgeVal}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Manual link */}
      <div className="px-2 pb-2">
        <NavLink
          to="/manual"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150
            ${isActive ? 'bg-primary-light text-primary' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`
          }
        >
          <BookIcon className="w-4 h-4 shrink-0 text-gray-400" />
          <span>คู่มือการใช้งาน</span>
        </NavLink>
      </div>

      {/* Credit */}
      <div className="px-4 py-3 border-t border-gray-100 text-center space-y-0.5">
        <div className="text-xs font-medium text-gray-500">Chokchai Thainkhao</div>
        <div className="text-gray-400" style={{ fontSize: 10 }}>Developer · AI Assisted (Claude)</div>
        <div className="text-gray-300" style={{ fontSize: 10 }}>v1.0.0 · © 2026</div>
      </div>
    </aside>
  );
}

/* Icon components */
function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}
function ListIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}
function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function ChartIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
function BellIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}
function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}
function GearIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
function BookIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}
function ChevronIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}
function ScaleIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18m0-18L7 8m5-5l5 5M5 8l-2.5 5.5a2.5 2.5 0 005 0L5 8zm14 0l-2.5 5.5a2.5 2.5 0 005 0L19 8zM5 8h0m14 0h0M8 21h8" />
    </svg>
  );
}
function BuildingIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}
